import { cva, type VariantProps } from 'class-variance-authority';

/** Named radius tokens authors can select. Boolean true/false stay as runtime aliases. */
export const IMAGE_NAMED_RADIUS_TOKENS = [
  'none',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'max',
] as const;

export type ImageNamedRadiusToken = (typeof IMAGE_NAMED_RADIUS_TOKENS)[number];

export const IMAGE_ROUNDED_SIDES = [
  'all',
  'top',
  'bottom',
  'start',
  'end',
] as const;

export type ImageRoundedSides = (typeof IMAGE_ROUNDED_SIDES)[number];

export const IMAGE_FRAME_MODES = ['bleed', 'inset'] as const;

export type ImageFrameMode = (typeof IMAGE_FRAME_MODES)[number];

export const IMAGE_INSET_PADDING_TOKENS = [
  'none',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
] as const;

export type ImageInsetPaddingToken =
  (typeof IMAGE_INSET_PADDING_TOKENS)[number];

export const IMAGE_ASPECT_TOKENS = [
  'auto',
  'square',
  'video',
  '4/3',
  '3/2',
  '3/4',
] as const;

export type ImageAspectToken = (typeof IMAGE_ASPECT_TOKENS)[number];

/** Complete class strings so Tailwind JIT emits per-side radius utilities. */
export const IMAGE_RADIUS_SIDE_CLASSES = {
  all: {
    none: 'rounded-none',
    xs: 'rounded-xs',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    max: 'rounded-max',
  },
  top: {
    none: 'rounded-none',
    xs: 'rounded-t-xs',
    sm: 'rounded-t-sm',
    md: 'rounded-t-md',
    lg: 'rounded-t-lg',
    xl: 'rounded-t-xl',
    max: 'rounded-t-max',
  },
  bottom: {
    none: 'rounded-none',
    xs: 'rounded-b-xs',
    sm: 'rounded-b-sm',
    md: 'rounded-b-md',
    lg: 'rounded-b-lg',
    xl: 'rounded-b-xl',
    max: 'rounded-b-max',
  },
  start: {
    none: 'rounded-none',
    xs: 'rounded-s-xs',
    sm: 'rounded-s-sm',
    md: 'rounded-s-md',
    lg: 'rounded-s-lg',
    xl: 'rounded-s-xl',
    max: 'rounded-s-max',
  },
  end: {
    none: 'rounded-none',
    xs: 'rounded-e-xs',
    sm: 'rounded-e-sm',
    md: 'rounded-e-md',
    lg: 'rounded-e-lg',
    xl: 'rounded-e-xl',
    max: 'rounded-e-max',
  },
} as const satisfies Record<
  ImageRoundedSides,
  Record<ImageNamedRadiusToken, string>
>;

export const IMAGE_ASPECT_CLASSES = {
  auto: '',
  square: 'aspect-square',
  video: 'aspect-video',
  '4/3': 'aspect-4/3',
  '3/2': 'aspect-3/2',
  '3/4': 'aspect-3/4',
} as const satisfies Record<ImageAspectToken, string>;

export const IMAGE_FRAME_CLASSES = {
  bleed: 'overflow-hidden',
  inset: 'overflow-hidden',
} as const satisfies Record<ImageFrameMode, string>;

/** Complete class strings so Tailwind JIT emits inset padding utilities. */
export const IMAGE_INSET_PADDING_CLASSES = {
  none: 'p-0',
  xs: 'p-xs',
  sm: 'p-sm',
  md: 'p-md',
  lg: 'p-lg',
  xl: 'p-xl',
} as const satisfies Record<ImageInsetPaddingToken, string>;

export const IMAGE_OBJECT_FIT_CLASSES = {
  cover: 'object-cover',
  contain: 'object-contain',
  'scale-down': 'object-scale-down',
  fill: 'object-fill',
  none: 'object-none',
} as const;

export function resolveNamedRadius(
  rounded: boolean | ImageNamedRadiusToken | null | undefined,
): ImageNamedRadiusToken {
  if (rounded === true) return 'xl';
  if (rounded === false || rounded == null) return 'none';
  return rounded;
}

export function imageRadiusClass(
  rounded: boolean | ImageNamedRadiusToken | null | undefined,
  roundedSides: ImageRoundedSides = 'all',
): string {
  return IMAGE_RADIUS_SIDE_CLASSES[roundedSides][resolveNamedRadius(rounded)];
}

/**
 * HEN Image variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const imageVariants = cva(
  ['object-cover', 'transition-opacity duration-200'],
  {
    variants: {
      rounded: {
        true: 'rounded-xl',
        false: 'rounded-none',
        none: 'rounded-none',
        xs: 'rounded-xs',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        max: 'rounded-max',
      } satisfies Record<'true' | 'false' | ImageNamedRadiusToken, string>,
      objectFit: {
        cover: 'object-cover',
        contain: 'object-contain',
        'scale-down': 'object-scale-down',
        fill: 'object-fill',
        none: 'object-none',
      },
    },
    defaultVariants: {
      rounded: false,
      objectFit: 'cover',
    },
  },
);

export type ImageVariantProps = VariantProps<typeof imageVariants>;

export const IMAGE_OVERLAY_POSITIONS = [
  'top-right',
  'top-left',
  'bottom-right',
  'bottom-left',
] as const;

export type ImageOverlayPosition = (typeof IMAGE_OVERLAY_POSITIONS)[number];

export const IMAGE_OVERLAY_POSITION_CLASSES: Record<
  ImageOverlayPosition,
  string
> = {
  'top-right': 'top-xxs right-xxs',
  'top-left': 'top-xxs left-xxs',
  'bottom-right': 'bottom-xxs right-xxs',
  'bottom-left': 'bottom-xxs left-xxs',
};
